import { projectDocumentBody } from './document-migration.js';
import {
  loadDocument, type DocumentKeys, type DocumentManifests,
} from './document-persistence.js';
import type { DerivedRoleProvider } from './derived-roles.js';
import type { Scope } from './scope.js';

/** Document vocabulary stays here; the generic derived-role boundary never learns it. */
export function documentBodyProvider(
  keys: DocumentKeys, manifests: DocumentManifests,
  supports: (scope: Scope) => boolean = () => true,
): DerivedRoleProvider {
  return {
    supports,
    async derive({ reader, object }) {
      /**
       * A note nobody has opened has no canonical document, and that must cost
       * this object its `body` — not the page.
       *
       * `loadDocument` answers `undefined` for an object the document manifest
       * does not address, which is the ordinary case and needs no guard. It
       * THROWS for an object that has a document and no blocks, and this
       * provider runs inside one `store.read` for the whole scope: an
       * uncaught throw there rejects the read, and the render with it. One
       * half-initialised note would take out the collection.
       *
       * The message test rather than a bare `catch` is the precedent
       * `mountDocumentCanvases` already sets, and it is the point: only the
       * not-initialised case is absorbed. A malformed graph is still a
       * refusal, raised where a person can see it, because a provider that
       * swallowed every error would report "no body" for a note whose document
       * is broken and give nobody a reason to look.
       *
       * NOT a fix for a measured failure — `e2e/cross-view.mjs` is 8/8 without
       * it. It is the difference between "one note has no body" and "the page
       * does not render", and that difference is worth the six lines.
       */
      let content;
      try {
        content = await loadDocument(reader, object.id, keys, manifests);
      } catch (error) {
        if (!(error instanceof Error)
          || !error.message.includes('a document has at least one block')) throw error;
      }
      return content
        ? [{ role: 'body', kind: 'text', value: projectDocumentBody(content) }]
        : [];
    },
  };
}
